import { formatBytes } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Spinner } from '@hvac/ui';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import {
  documentDetailQuery,
  documentKeys,
  requestDownloadUrl,
  retryDocument,
} from '../../shared/api/document-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { DocumentViewer } from './document-viewer.js';
import { ExtractionPanel } from './extraction-panel.js';

/**
 * One document: what it is, what happened to it, and every version of it.
 *
 * The viewer and the metadata sit side by side because they are read together
 *, "is this the right invoice, and when did it arrive" is one question.
 */
export function DocumentDetailPage() {
  const { documentId } = useParams({ from: '/protected/documents/$documentId' });
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const detail = useQuery(documentDetailQuery(organizationId, documentId));

  const versionId = selectedVersionId ?? detail.data?.versions.find((v) => v.isCurrent)?.id ?? null;

  // A fresh signed URL per version. Cached just under its lifetime so
  // switching back and forth does not mint a new one every time.
  const download = useQuery({
    queryKey: ['document-file', organizationId, documentId, versionId],
    queryFn: () =>
      requestDownloadUrl(organizationId, documentId, {
        ...(versionId === null ? {} : { versionId }),
        expiresInSeconds: 900,
      }),
    enabled: detail.isSuccess && versionId !== null,
    staleTime: 840_000,
  });

  if (detail.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-6 text-brand-600" label="Loading document" />
      </div>
    );
  }

  if (detail.isError) {
    return (
      <Alert tone="danger" title="Could not load this document">
        {detail.error instanceof Error ? detail.error.message : 'It may have been archived.'}
      </Alert>
    );
  }

  const document = detail.data;
  const canRequestExtraction =
    session.organizations
      .find((entry) => entry.organization.id === organizationId)
      ?.permissions.includes('document:update') ?? false;
  const activeVersion = document.versions.find((version) => version.id === versionId);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/documents" className="text-sm text-content-muted hover:underline">
            ← All documents
          </Link>
          <h1 className="mt-1 truncate text-xl font-semibold text-content">
            {document.filename ?? 'Untitled document'}
          </h1>
        </div>

        <div className="flex gap-2">
          {document.ingestionState === 'failed' ? (
            <Button
              variant="secondary"
              onClick={() => {
                void retryDocument(organizationId, documentId).then(() =>
                  queryClient.invalidateQueries({
                    queryKey: documentKeys.detail(organizationId, documentId),
                  }),
                );
              }}
            >
              Retry
            </Button>
          ) : null}
          <Button
            onClick={() => {
              if (download.data !== undefined) {
                window.open(download.data.downloadUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            disabled={download.data === undefined}
          >
            Download original
          </Button>
        </div>
      </div>

      {document.failureReason !== null ? (
        <Alert tone="danger" title="This document could not be processed">
          {document.failureReason}
        </Alert>
      ) : null}

      {document.duplicateOfDocumentId !== null ? (
        <Alert tone="warning" title="Kept as a deliberate duplicate">
          {document.duplicateOverrideReason ?? 'A copy of an earlier document.'}
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="h-[36rem]">
          {download.isPending ? (
            <div className="flex h-full items-center justify-center rounded-[var(--radius-surface)] border border-border">
              <Spinner className="size-6 text-brand-600" label="Preparing document" />
            </div>
          ) : download.data === undefined ? (
            <Alert tone="danger">The file could not be opened.</Alert>
          ) : (
            <DocumentViewer
              url={download.data.downloadUrl}
              mimeType={download.data.mimeType}
              filename={download.data.filename}
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <ExtractionPanel
            organizationId={organizationId}
            documentId={documentId}
            canRequest={canRequestExtraction}
          />

          <Card>
            <CardHeader title="Details" />
            <CardBody>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <Detail label="Status" value={document.ingestionState.replace(/_/g, ' ')} />
                <Detail label="Source" value={document.source.replace(/_/g, ' ')} />
                <Detail
                  label="Received"
                  value={new Date(document.receivedAt).toLocaleString('en-CA')}
                />
                <Detail
                  label="Uploaded by"
                  value={document.uploader.fullName ?? document.uploader.email ?? 'Unknown'}
                />
                <Detail label="Type" value={document.mimeType ?? 'Unknown'} />
                <Detail
                  label="Size"
                  value={document.byteSize === null ? 'Unknown' : formatBytes(document.byteSize)}
                />
                {document.pageCount !== null ? (
                  <Detail label="Pages" value={String(document.pageCount)} />
                ) : null}
                <dt className="text-content-muted">SHA-256</dt>
                <dd className="break-all font-mono text-xs text-content">
                  {document.sha256 ?? 'Not computed'}
                  {activeVersion?.checksumVerifiedAt != null ? (
                    <span className="ml-1 text-success">· verified</span>
                  ) : activeVersion?.checksumVerificationFailedAt != null ? (
                    <span className="ml-1 text-danger">· mismatch</span>
                  ) : (
                    <span className="ml-1 text-content-subtle">· awaiting verification</span>
                  )}
                </dd>
              </dl>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Versions"
              description="Originals are never edited. A better scan is a new version."
            />
            <CardBody className="flex flex-col gap-1">
              {document.versions.map((version) => (
                <button
                  key={version.id}
                  type="button"
                  onClick={() => setSelectedVersionId(version.id)}
                  className={`rounded-[var(--radius-control)] px-2 py-1.5 text-left text-sm hover:bg-surface-sunken ${
                    version.id === versionId ? 'bg-surface-sunken font-medium' : ''
                  }`}
                >
                  Version {version.versionNumber}
                  {version.isCurrent ? ' · current' : ''}
                  <span className="block text-xs text-content-muted">
                    {new Date(version.createdAt).toLocaleString('en-CA')} ·{' '}
                    {formatBytes(version.byteSize)}
                    {version.reason === null ? '' : ` · ${version.reason}`}
                  </span>
                </button>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="History"
              description="Every state change, logged by the database itself."
            />
            <CardBody>
              <ol className="flex flex-col gap-2 text-sm">
                {document.stateEvents.map((event) => (
                  <li key={event.id} className="flex items-baseline justify-between gap-3">
                    <span className="text-content">
                      {event.fromState === null
                        ? event.toState.replace(/_/g, ' ')
                        : `${event.fromState.replace(/_/g, ' ')} → ${event.toState.replace(/_/g, ' ')}`}
                    </span>
                    <span className="shrink-0 text-xs text-content-subtle">
                      {new Date(event.occurredAt).toLocaleTimeString('en-CA')}
                      {event.durationMs === null ? '' : ` · ${formatDuration(event.durationMs)}`}
                    </span>
                  </li>
                ))}
              </ol>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-content-muted">{label}</dt>
      <dd className="text-content capitalize">{value}</dd>
    </>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 60_000)} min`;
}
